<template>
  <Teleport
    v-if="isMounted"
    to="#teleported"
    :disabled="disableTeleport"
  >
    <dialog
      :id="id"
      :key="id"
      ref="content"
      class="v-overlay-wrapper"
      :aria-label="title"
      @click.self="closeModal()"
      @keydown="trapFocusInModal($event)"
    >
      <div
        v-if="isCurrentModalOverlayOpen"
        class="v-overlay-content"
        :style="{ width: width }"
      >
        <div
          class="grid-x v-overlay-header"
          :class="title ? 'align-justify' : 'align-right'"
        >
          <h2
            v-if="title"
            class="h4 cell large-11 small-11 medium-11"
            :aria-label="title"
            :style="{ visibility: hiddenScreenreaderTitle ? 'hidden' : undefined, }"
          >
            {{ title }}
          </h2>

          <button
            type="button"
            class="v-overlay-close-btn cell large-1 medium-1 small-1"
            aria-label="close"
            @click.stop="closeModal()"
          >
            <Icon :icon="csgClose" />
          </button>
        </div>

        <div
          :class="{
            'v-header-visible': title && !hiddenScreenreaderTitle,
          }"
          class="v-overlay-body"
        >
          <div class="v-overlay-content-container">
            <slot name="body" />
          </div>
        </div>
        <div
          v-if="$slots.footer"
          class="v-overlay-footer-container"
        >
          <slot name="footer" />
        </div>
      </div>
    </dialog>
  </Teleport>
</template>

<script>
import { nextTick } from 'vue';
import { mapMutations, mapState } from 'vuex';
import { csgClose } from '@csgSvgIcons/icons';
import SET_MODAL_OVERLAY_VISIBILITY from '../../store_modules/modalOverlay/mutations';
import focusTrap from '../../utils/focusTrap';
import Icon from '../icon';

export default {
  name: 'ModalOverlay',
  props: {
    id: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    hiddenScreenreaderTitle: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      required: false,
      default: '',
    },
    disableTeleport: {
      type: Boolean,
      required: false,
      default: false,
    },
    handleTopLayer: {
      type: String,
      required: false,
      default: 'showModal',
      validator: (val) => ['showModal', 'show'].includes(val),
    },
  },
  components: {
    Icon,
  },
  emits: ['close'],
  data() {
    return {
      isMounted: false,
      isDialogPolyfillRegistered: false,
      csgClose,
    };
  },
  computed: {
    ...mapState('modalOverlayStore', ['showModal']),
    isCurrentModalOverlayOpen() {
      return this.showModal[this.id];
    },
  },
  watch: {
    showModal: {
      immediate: true,
      deep: true,
      async handler(showModal) {
        await nextTick();
        const shouldLoad = typeof window !== 'undefined' && typeof window.HTMLDialogElement === 'undefined' && !this.isDialogPolyfillRegistered;
        if (shouldLoad) {
          this.loadDialogPolyfill();
        }
        if (Object.keys(showModal).length !== 0) {
          if (this.isCurrentModalOverlayOpen) {
            this.disablePageScroll();
            if (this.handleTopLayer === 'showModal') {
              if (!this.$refs?.content?.open) {
                this.$refs?.content?.showModal();
              }
            } else {
              this.$refs?.content?.show();
            }
          } else if (this.$refs?.content?.open) {
            this.closeModal();
          }
        }
      },
    },
  },
  mounted() {
    this.isMounted = true;
  },
  beforeUnmount() {
    this.closeModal();
  },
  methods: {
    ...mapMutations('modalOverlayStore', {
      toggleModal: SET_MODAL_OVERLAY_VISIBILITY,
    }),
    loadDialogPolyfill() {
      this.isDialogPolyfillRegistered = true;
      const dialogPolyfill = require('dialog-polyfill').default;
      dialogPolyfill.registerDialog(this.$refs?.content);
      const registerFocusRestoreDialog = require('../../utils/registerFocusRestoreDialog').default;
      registerFocusRestoreDialog(this.$refs?.content);
    },
    closeModal() {
      if (this.$refs.content.open) {
        if (this.isCurrentModalOverlayOpen) {
          this.toggleModal(this.id);
        }
        this.$emit('close', this.id);
        this.$refs?.content?.close();
        this.enablePageScroll();
      }
    },
    trapFocusInModal(event) {
      const TAB_KEYCODE = 9;
      if (event.key === 'Escape') {
        this.closeModal();
      } else if (event.key === 'Tab' || event.keyCode === TAB_KEYCODE) {
        focusTrap(event, this.$refs?.content);
      }
    },
    disablePageScroll() {
      if (typeof window !== 'undefined') {
        if (window.document.body.dataset.retainedOverflow !== undefined) {
          return;
        }
        window.document.body.dataset.retainedOverflow = window.document.body.style.overflow;
        window.document.body.style.overflow = 'hidden';
      }
    },
    enablePageScroll() {
      if (typeof window !== 'undefined') {
        window.document.body.style.overflow = window.document.body.dataset.retainedOverflow;
        delete window.document.body.dataset.retainedOverflow;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
dialog[open]{
  display:flex;
  border:none;
  max-width:none;
  max-height:none;
}
dialog::backdrop{
  background:none;
}
dialog + .backdrop {
  background: $overlay-color;
}
.v-overlay-wrapper {
  display:none;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 10;
  overflow-y: auto;
  background-color: $overlay-color;
  padding: rem-calc(32) 0;
  @include breakpoint(small only) {
    padding: 0;
  }
  .v-overlay-content {
    align-self: center;
    overflow-y: auto;
    max-width: 70%;
    max-height: none;
    margin: auto;
    vertical-align: middle;
    position: relative;
    display: flex;
    flex-direction: column;
    padding: rem-calc(48);
    background: $white;

    @include breakpoint(small down) {
      -webkit-overflow-scrolling: auto; //for ios
      width: 100% !important;
      height: 100%;
      max-width: 100%;
      border: none;
      padding: rem-calc(32) rem-calc(20) rem-calc(48) rem-calc(20);
    }
    .v-overlay-header {
      align-items: center;
    }
    .v-overlay-body {
      text-align: left;
      margin-top: rem-calc(24);
      @include breakpoint(small only) {
        max-height: none; //necessary FOR IOS 9 scroll bug
        @supports (-webkit-overflow-scrolling: touch) {
          height: 100%;
        }
      }
    }
    .v-overlay-close-btn {
      cursor: pointer;
      border: none;
      width: 24px;
      height: 24px;
    }
    .v-overlay-footer-container {
      padding-top: rem-calc(24);
      @include breakpoint(small only) {
        margin: auto 0 0 0;
      }
    }
    .v-header-visible.v-overlay-body {
      margin-top: rem-calc(32);
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
.v-overlay-wrapper {
  z-index: 30; // need to be higher than macys mobile header
}
</style>

<style lang="scss" brand="bcom" scoped>
.v-overlay-wrapper {
  z-index: 30;
}
</style>
